/**
 * @file apps/web/src/components/PortalShell.tsx
 * @description Client portal shell layout.
 * Lighter than AppShell — white topbar with client branding, horizontal nav,
 * and a content area. Redirects to /portal/login if not authenticated.
 */

import { Outlet, NavLink, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, ClipboardList, FileText, CheckCircle, PenLine, LogOut,
} from 'lucide-react';
import { usePortalAuth } from '../lib/portalAuth.js';
import { supabase } from '../lib/supabase.js';

interface NavItem {
  to:    string;
  label: string;
  icon:  React.ReactNode;
  exact?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/portal/dashboard',  label: 'Overview',    icon: <LayoutDashboard size={15} />, exact: true },
  { to: '/portal/scripts',    label: 'Test Scripts', icon: <ClipboardList size={15} /> },
  { to: '/portal/fdd',        label: 'Documents',    icon: <FileText size={15} /> },
  { to: '/portal/approvals',  label: 'Approvals',    icon: <CheckCircle size={15} /> },
  { to: '/portal/signoff',    label: 'Sign-off',     icon: <PenLine size={15} /> },
];

export function PortalShell() {
  const { user, isLoggedIn, setUser } = usePortalAuth();

  if (!isLoggedIn) {
    return <Navigate to="/portal/login" replace />;
  }

  const initials = (user!.fullName)
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--gray)' }}>
      {/* ------------------------------------------------------------------ */}
      {/* Topbar                                                              */}
      {/* ------------------------------------------------------------------ */}
      <header
        className="h-14 flex items-center px-6 gap-4 border-b sticky top-0 z-30"
        style={{ backgroundColor: 'white', borderColor: 'rgba(8,17,31,0.08)' }}
      >
        {/* Logo */}
        <div className="flex items-center gap-2 mr-4">
          <div
            className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-mono font-bold shrink-0"
            style={{ backgroundColor: 'var(--blue)' }}
          >
            V
          </div>
          <span className="font-display text-base font-bold tracking-tight" style={{ color: 'var(--ink)' }}>
            Veridox
          </span>
          <span
            className="text-[10px] font-mono px-1.5 py-0.5 rounded"
            style={{ backgroundColor: 'var(--sky)', color: 'var(--blue)' }}
          >
            Client Portal
          </span>
        </div>

        {/* Nav */}
        <nav className="flex items-center gap-0.5 flex-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              className={({ isActive }) =>
                [
                  'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors',
                  isActive
                    ? 'text-blue bg-sky'
                    : 'text-muted hover:text-ink hover:bg-gray',
                ].join(' ')
              }
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="flex items-center gap-2.5 ml-auto">
          <div className="text-right hidden sm:block">
            <p className="text-xs font-medium text-ink leading-none">{user!.fullName}</p>
            <p className="text-[10px] font-mono text-muted mt-0.5">{user!.clientName}</p>
          </div>
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-mono font-bold shrink-0"
            style={{ backgroundColor: 'var(--blue)' }}
          >
            {initials}
          </div>
          <button
            onClick={() => { void supabase.auth.signOut(); setUser(null); }}
            className="btn-icon text-muted hover:text-ink ml-1"
            title="Sign out"
          >
            <LogOut size={14} />
          </button>
        </div>
      </header>

      {/* ------------------------------------------------------------------ */}
      {/* Content area                                                        */}
      {/* ------------------------------------------------------------------ */}
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}
